import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import PerformanceInboxPage from '../performance-inbox/PerformanceInboxPage';
import MyGoalsPage from '../period-goals/MyGoalsPage';
import OneOnOnesPage from '../one-on-ones/OneOnOnesPage';
import MyPeriodResultsPage from '../my-period-results/MyPeriodResultsPage';
import MeSummary from './components/MeSummary';
import { ME_TABS } from './config';
import {
  PageShell, ShellHeader, TabBody, TabStrip,
} from './styles';

// PM Analitik Faz 2 (spec §8) — E18 "Performansım" (/performance/me): çalışanın kendi çalışma alanı. Self-servis (PM aboneliği +
// koltuk; ayrıcalık aranmaz). Sekmeler: Özet | Görevlerim | Hedeflerim | Görüşmelerim | Sonuçlarım — son dördü MEVCUT sayfaların
// bileşenlerini gömer (Gelen Kutum, Hedeflerim, 1:1 Görüşmelerim, Dönem Sonuçlarım; ikiz kod yok, menüdeki eski girişler durur).
// Sekme, Özet dönemi ve açılacak hedef URL'de tutulur (?tab=&period=&goal=) — geri dönüşte korunur. Tek kaydırma: sekme gövdesi.
export default function PerformanceMePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = ME_TABS.includes(params.get('tab')) ? params.get('tab') : 'summary';
  const periodId = params.get('period') || null;
  const goalId = params.get('goal') || null;

  const update = useCallback((patch, push = false) => {
    const next = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    const search = next.toString();
    const target = { pathname: location.pathname, search: search ? `?${search}` : '' };
    if (push) history.push(target);
    else history.replace(target);
  }, [history, location.pathname, location.search]);

  const tabs = useMemo(() => ME_TABS.map((key) => ({ key, label: t(`performance.workspace.me.tabs.${key}`) })), [t]);

  const changeTab = useCallback((key) => update({ tab: key === 'summary' ? null : key, goal: null }, true), [update]);

  // Özet kartlarından sekme geçişi (hedef kartı → Hedeflerim'de o hedef; itiraz → Sonuçlarım).
  const openFromSummary = useCallback((nextTab, extra = {}) => {
    update({
      tab: nextTab === 'summary' ? null : nextTab,
      goal: extra.goal || null,
      ...(extra.period ? { period: extra.period } : {}),
    }, true);
  }, [update]);

  return (
    <PageShell data-page="performance-me">
      <ShellHeader>
        <Breadcrumbs items={[{ label: t('performance.workspace.me.title') }]} />
        <TabStrip>
          <Tabs tabs={tabs} activeTab={tab} onChange={changeTab} />
        </TabStrip>
      </ShellHeader>
      <TabBody data-tab={tab}>
        {tab === 'summary' ? (
          <MeSummary periodId={periodId} onPeriodChange={(id) => update({ period: id })} onOpen={openFromSummary} />
        ) : null}
        {tab === 'tasks' ? <PerformanceInboxPage /> : null}
        {tab === 'goals' ? <MyGoalsPage initialPeriodId={periodId} openGoalId={goalId} tableBodyMaxHeight="none" /> : null}
        {tab === 'meetings' ? <OneOnOnesPage tableBodyMaxHeight="none" /> : null}
        {tab === 'results' ? <MyPeriodResultsPage initialPeriodId={periodId} /> : null}
      </TabBody>
    </PageShell>
  );
}
